import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Paper, Grid, CardActionArea, CircularProgress } from '@mui/material';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import { useAuth } from '../../auth/AuthContext';
import { PRODUCTS, getExplicitProducts, setActiveProduct } from '../../constants/productAccess';

const TOOL_CARDS = {
  [PRODUCTS.SCHOOL_EXPLORER]: {
    title: 'School Explorer',
    description: 'Portfolio analysis, school summaries, and geographic/competitive/projections explorers.',
    icon: <MapsHomeWorkOutlinedIcon sx={{ fontSize: 48 }} />,
    landingPath: '/dashboard',
  },
  [PRODUCTS.CITY_EXPLORER]: {
    title: 'City Explorer',
    description: 'Interactive market-access maps: legislative context and school-access gap analysis by city.',
    icon: <LocationCityOutlinedIcon sx={{ fontSize: 48 }} />,
    landingPath: '/city-explorer',
  },
};

const SelectTool = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const products = getExplicitProducts(user);

  const handleSelect = (product) => {
    setActiveProduct(product);
    navigate(TOOL_CARDS[product].landingPath);
  };

  // Someone with only one product who lands here directly (e.g. browser
  // back button) shouldn't see a single-card picker - just send them on.
  useEffect(() => {
    if (products.length <= 1) {
      handleSelect(products[0] || PRODUCTS.SCHOOL_EXPLORER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (products.length <= 1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#F9FAFB',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <img src="/images/edc_logo_circle.png" alt="EDC Logo" style={{ height: 160, objectFit: 'contain' }} />
        </Box>
        <Typography
          component="h1"
          variant="h4"
          align="center"
          sx={{ fontWeight: 600, color: '#111827', mb: 1 }}
        >
          Which tool would you like to use?
        </Typography>
        <Typography align="center" sx={{ color: 'text.secondary', mb: 5 }}>
          You have access to more than one EDC tool. You can switch between them anytime from the account menu.
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {products.map((product) => {
            const card = TOOL_CARDS[product];
            if (!card) return null;
            return (
              <Grid item xs={12} sm={6} key={product}>
                <Paper
                  elevation={0}
                  sx={{
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: 2,
                    height: '100%',
                    transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0px 4px 16px rgba(67, 56, 202, 0.15)',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleSelect(product)}
                    sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                  >
                    <Box sx={{ color: 'primary.main', mb: 2 }}>{card.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      {card.description}
                    </Typography>
                  </CardActionArea>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default SelectTool;
